// ============================================================================
// LandingHero — the striking, energetic above-the-fold. Mobile-first: a single
// stacked column at 390 (copy → graphic → live card), expanding to a two-up
// stage on lg. The custom <PulseGraphic/> supplies the real, looping motion;
// the copy + CTAs stagger in on load. CTAs are personalised for a returning
// trainer via the optional-first session probe — but the page is fully usable
// for a brand-new visitor regardless. Reuses locked Pulse Button/Badge/StatTile.
// ============================================================================
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Badge, Button, StatTile } from "../../components";
import { PulseGraphic } from "./PulseGraphic";
import { CLIENT_LOGIN_PATH } from "../../client/routes";
import type { ReturningTrainer } from "./useReturningTrainer";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.08 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LandingHero({ session }: { session: ReturningTrainer }) {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  const proof = [
    t("hero.proof.clients"),
    t("hero.proof.calendar"),
    t("hero.proof.nutrition"),
    t("hero.proof.messaging"),
    t("hero.proof.metrics"),
    t("hero.proof.reports"),
  ];

  // Returning trainers go straight to /app; new visitors are pushed to create
  // an account (with a secondary sign-in path below for those who already have).
  const primaryHref = session.isReturning ? "/app" : "/signup";
  const primaryLabel = session.isReturning
    ? t("hero.resume")
    : t("hero.signUpCta");

  return (
    <section className="relative isolate overflow-hidden bg-paper text-body">
      {/* Atmosphere: soft coral + violet radial washes on the light canvas. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(820px 480px at 88% -4%, rgba(255,138,61,0.18), transparent 60%)," +
            "radial-gradient(720px 480px at -4% 16%, rgba(123,91,255,0.16), transparent 58%)",
        }}
      />
      {/* Faint masked grid for depth. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-grid-light bg-grid"
        style={{
          maskImage:
            "radial-gradient(ellipse 85% 75% at 50% 0%, #000 28%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 85% 75% at 50% 0%, #000 28%, transparent 72%)",
        }}
      />

      <div className="mx-auto grid max-w-shell items-center gap-10 px-gutter pb-14 pt-12 sm:pt-16 md:pb-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 lg:pt-20">
        {/* ---- Copy column ---- */}
        <motion.div
          variants={reduce ? undefined : stagger}
          initial={reduce ? undefined : "hidden"}
          animate={reduce ? undefined : "show"}
        >
          <motion.div variants={reduce ? undefined : rise}>
            <Badge tone="volt" solid dot className="mb-5">
              {t("hero.badge")}
            </Badge>
          </motion.div>

          <motion.h1
            variants={reduce ? undefined : rise}
            className="font-display font-extrabold tracking-tightest leading-[0.98] text-[clamp(2.5rem,1.4rem+5.5vw,5rem)]"
          >
            <span className="block">{t("hero.headlineLead")}</span>
            <span className="text-gradient-volt block">
              {t("hero.headlineHighlight")}
            </span>
            <span className="block text-body-soft">{t("hero.headlineTrail")}</span>
          </motion.h1>

          <motion.p
            variants={reduce ? undefined : rise}
            className="mt-5 max-w-xl text-base leading-relaxed text-body-muted sm:text-lg"
          >
            {t("hero.subhead")}
          </motion.p>

          {session.isReturning && session.firstName && (
            <motion.p
              variants={reduce ? undefined : rise}
              className="mt-4 inline-flex items-center gap-2 rounded-pill bg-violet-soft px-3 py-1.5 text-sm font-bold text-ember-600"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-violet" aria-hidden />
              {t("hero.welcomeBack", { name: session.firstName })}
            </motion.p>
          )}

          <motion.div
            variants={reduce ? undefined : rise}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
          >
            <Link to={primaryHref} className="sm:w-auto">
              <Button size="lg" block iconRight={<ArrowRight />}>
                {primaryLabel}
              </Button>
            </Link>
            {/* Returning trainers (whose primary CTA resumes their studio) keep
                the feature anchor as the secondary action. */}
            {session.isReturning && (
              <a href="#features" className="sm:w-auto">
                <Button size="lg" variant="ghost" block>
                  {t("hero.secondaryCta")}
                </Button>
              </a>
            )}
          </motion.div>

          {/* TWO clear login doorways — trainer studio + client portal. Always
              present (a new visitor signs up; an existing trainer OR client signs
              in through the right door). */}
          {!session.isReturning && (
            <motion.div
              variants={reduce ? undefined : rise}
              className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm"
            >
              <span className="font-medium text-body-soft">
                {t("hero.loginPrompt")}
              </span>
              <Link
                to="/login"
                className="font-bold text-coral transition-colors hover:text-volt-600"
              >
                {t("hero.trainerLogin")}
              </Link>
              <span className="text-body-faint" aria-hidden>
                ·
              </span>
              <Link
                to={CLIENT_LOGIN_PATH}
                className="font-bold text-coral transition-colors hover:text-volt-600"
              >
                {t("hero.clientLogin")}
              </Link>
            </motion.div>
          )}
        </motion.div>

        {/* ---- Visual column: custom animated graphic + floating live card ---- */}
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <PulseGraphic className="mx-auto block h-auto w-full max-w-[440px]" />

          {/* Floating live KPI card — "show, don't tell". Overlaps the graphic
              on larger screens; flows beneath it comfortably on mobile. */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 26, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: reduce ? 0 : -2 }}
            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
            className="relative z-10 mx-auto -mt-4 w-full max-w-xs sm:absolute sm:-bottom-6 sm:right-0 sm:mt-0 sm:max-w-[15rem]"
          >
            <div className="rounded-xl bg-surface p-4 shadow-lg ring-1 ring-line">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-display text-sm font-bold text-body">
                  {t("hero.live.title")}
                </span>
                <Badge tone="success" dot>
                  {t("hero.live.status")}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <StatTile
                  tone="volt"
                  label={t("hero.live.sessions")}
                  value={18}
                  delta="+3"
                  deltaTone="up"
                  className="!p-3"
                />
                <StatTile
                  accent="violet"
                  label={t("hero.live.rpe")}
                  value={7.4}
                  decimals={1}
                  delta="+0.6"
                  className="!p-3"
                />
              </div>
            </div>
            <div
              aria-hidden
              className="absolute -right-2.5 -top-2.5 -z-10 h-20 w-20 rotate-12 rounded-xl bg-grad-violet shadow-ember"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Light proof marquee strip. */}
      <div className="relative overflow-hidden border-t border-line py-4">
        <div
          className={
            "flex w-max gap-10 px-gutter " + (reduce ? "" : "animate-marquee")
          }
        >
          {[...proof, ...proof].map((word, i) => (
            <span
              key={i}
              className="inline-flex items-center font-display text-xs font-bold uppercase tracking-kicker text-body-soft sm:text-sm"
            >
              {word}
              <span className="ml-10 text-coral">/</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
